'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import SafeImage from '../SafeImage';
import { ChevronRight, Clock, Users, Star, Info, Loader2, Sparkles } from 'lucide-react';
import { UNIFIED_STORY_TEMPLATES } from '@/lib/unified-story-templates';
import { getOrCreateSessionId } from '@/utils/session-manager';

interface Character {
  name: string;
  age: string;
  gender: string;
  isMainCharacter?: boolean;
}

interface StoryRecommendation {
  id: string;
  category: string;
  title: string;
  description: string;
  ageRange: { min: number; max: number };
  suitabilityScore: number;
  reasoning: string;
}

interface MultiCharacterStorySelectionStepProps {
  characters: Character[];
  relationshipType?: string;
  photoBase64?: string;
  photoUrl?: string; // Accept either base64 or URL
  onSelect: (template: any, pageCount: number) => void;
}

export default function MultiCharacterStorySelectionStep({
  characters,
  relationshipType = 'siblings',
  photoBase64,
  photoUrl,
  onSelect
}: MultiCharacterStorySelectionStepProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<StoryRecommendation | null>(null);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<StoryRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Get primary character info for display
  const primaryCharacter = characters.find(c => c.isMainCharacter) || characters[0];

  useEffect(() => {
    loadStoryRecommendations();
  }, []);

  const loadStoryRecommendations = async () => {
    // Use either base64 or URL
    const photoData = photoBase64 || photoUrl;

    if (!photoData) {
      console.warn('⚠️ [MULTI-STORY-SELECTION] No photo available, using fallback custom story option');
      setRecommendations([{
        id: 'custom-story',
        category: 'custom',
        title: "I'll Make My Own Story",
        description: "Create a completely custom adventure tailored to your unique ideas and imagination.",
        ageRange: { min: 0, max: 100 },
        suitabilityScore: 0,
        reasoning: "Custom option for maximum personalization"
      }]);
      setLoading(false);
      return;
    }

    try {
      console.log('📚 [MULTI-STORY-SELECTION] Fetching AI story recommendations...');
      console.log('📚 [MULTI-STORY-SELECTION] photoBase64:', !!photoBase64);
      console.log('📚 [MULTI-STORY-SELECTION] photoUrl:', !!photoUrl);
      console.log('📚 [MULTI-STORY-SELECTION] characters:', characters?.length || 0);

      const response = await fetch('/api/books/suggest-stories', {
        method: 'POST',
        // X3/G6: the route needs an identity and never receives the photo (suggestions use names and ages only).
        headers: { 'Content-Type': 'application/json', ...(getOrCreateSessionId() ? { 'x-session-id': getOrCreateSessionId() as string } : {}) },
        body: JSON.stringify({
          characters: characters.map(c => ({
            name: c.name,
            age: parseInt(c.age) || 8,
            gender: c.gender,
            isMainCharacter: c.isMainCharacter
          })),
          childDetails: {
            firstName: primaryCharacter?.name || 'Child',
            age: parseInt(primaryCharacter?.age || '8'),
            gender: primaryCharacter?.gender
          }
        })
      });

      console.log('📚 [MULTI-STORY-SELECTION] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ [MULTI-STORY-SELECTION] API error:', errorData);
        throw new Error(errorData.error || 'Failed to get story recommendations');
      }

      const data = await response.json();
      console.log(`✅ [MULTI-STORY-SELECTION] Loaded ${data.recommendations.length} recommendations`);

      setRecommendations(data.recommendations);
      setLoading(false);
    } catch (err) {
      console.error('❌ [MULTI-STORY-SELECTION] Error loading recommendations:', err);
      setError(`Failed to load story recommendations: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };

  // Filter by category
  const filteredRecommendations = selectedCategory === 'all'
    ? recommendations
    : recommendations.filter(r => r.category === selectedCategory);

  // Extract unique categories from recommendations
  const categories = [
    { id: 'all', label: 'All Stories', icon: '📚' },
    ...Array.from(new Set(recommendations.map(r => r.category)))
      .filter(cat => cat !== 'custom')
      .map(cat => ({
        id: cat,
        label: cat.charAt(0).toUpperCase() + cat.slice(1),
        icon: getCategoryIcon(cat)
      }))
  ];

  const handleTemplateSelect = (recommendation: StoryRecommendation) => {
    setSelectedTemplate(recommendation);

    // Look up the unified template to get conflict/antagonist data
    const unifiedTemplate = UNIFIED_STORY_TEMPLATES.find(t => t.id === recommendation.id);

    // Convert recommendation to template format expected by wizard
    const template = {
      id: recommendation.id,
      category: recommendation.category,
      title: recommendation.title,
      description: recommendation.description,
      ageRange: recommendation.ageRange,
      themeId: unifiedTemplate?.themeId || 'custom', // Use unified template's themeId if available
      thumbnailUrl: unifiedTemplate?.thumbnailUrl || '', // Use unified template's thumbnail if available
      characterCount: { min: characters.length, max: characters.length, ideal: characters.length },
      relationshipTypes: [relationshipType],
      // Include conflict/antagonist from unified template for dramatic arc guidance
      conflictType: unifiedTemplate?.conflictType,
      antagonist: unifiedTemplate?.antagonist,
      storyContext: {
        mainTheme: recommendation.category,
        additionalComments: unifiedTemplate?.storyContext?.additionalComments || recommendation.description,
        setting: unifiedTemplate?.storyContext?.setting,
        plotOutline: unifiedTemplate?.storyContext?.plotOutline
      }
    };

    console.log(`🎭 [MULTI-STORY-SELECTION] Selected template: ${recommendation.id}`);
    console.log(`🎭 [MULTI-STORY-SELECTION] conflictType: ${template.conflictType || 'none'}`);
    console.log(`🎭 [MULTI-STORY-SELECTION] antagonist: ${template.antagonist?.name || 'none'}`);

    // Pass default page count from unified template or 16
    const pageCount = unifiedTemplate?.defaultPageCount || 16;
    onSelect(template, pageCount);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-20"
        >
          <Loader2 className="w-16 h-16 mx-auto text-purple-600 animate-spin mb-4" />
          <h2 className="text-2xl font-bold text-purple-800 mb-2">
            <Sparkles className="inline w-6 h-6 mr-2" />
            Creating Personalized Stories
          </h2>
          <p className="text-gray-600">
            We're analyzing your photo to suggest perfect adventures for {characters.map(c => c.name).join(' and ')}...
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Stories</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadStoryRecommendations}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-purple-800 flex items-center justify-center gap-2">
          <Sparkles className="w-8 h-8 text-purple-600" />
          Adventures for {characters.map(c => c.name).join(' & ')}
        </h2>
        <p className="mt-2 text-gray-600 text-lg">
          <Users className="inline w-5 h-5 mr-1" />
          Personalized recommendations for {characters.length} characters
        </p>
      </motion.div>

      {/* Category Filter */}
      {categories.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6"
        >
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.label}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Story Recommendations Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecommendations.map((recommendation, index) => {
            const isSelected = selectedTemplate?.id === recommendation.id;
            const isCustom = recommendation.id === 'custom-story';

            return (
              <motion.div
                key={recommendation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className={`relative cursor-pointer transition-all transform ${
                  isSelected ? 'scale-105' : 'hover:scale-105'
                }`}
                onClick={() => handleTemplateSelect(recommendation)}
                onMouseEnter={() => setHoveredTemplate(recommendation.id)}
                onMouseLeave={() => setHoveredTemplate(null)}
              >
                <div className={`rounded-lg overflow-hidden shadow-lg border-2 h-full ${
                  isSelected
                    ? 'border-purple-500 ring-2 ring-purple-300'
                    : 'border-gray-200 hover:border-purple-300'
                } ${isCustom ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-white'}`}>

                  {/* Card Content */}
                  <div className="p-5">
                    {/* Suitability Score Badge */}
                    {!isCustom && (
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          <Star size={14} className="text-yellow-500 fill-yellow-500" />
                          <span className="text-xs font-semibold text-gray-600">
                            {recommendation.suitabilityScore}% Match
                          </span>
                        </div>
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                          {recommendation.category}
                        </span>
                      </div>
                    )}

                    {isSelected && (
                      <div className="absolute top-3 right-3 bg-purple-600 text-white rounded-full p-2">
                        <Star size={16} fill="white" />
                      </div>
                    )}

                    <h3 className="font-bold text-lg text-gray-800 mb-2 leading-tight">
                      {recommendation.title}
                    </h3>

                    <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                      {recommendation.description}
                    </p>

                    {!isCustom && (
                      <>
                        <div className="flex items-center text-xs text-gray-500 mb-2">
                          <span>Ages {recommendation.ageRange.min}-{recommendation.ageRange.max}</span>
                        </div>

                        {/* Reasoning (shown on hover or selection) */}
                        {(hoveredTemplate === recommendation.id || isSelected) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-3 pt-3 border-t border-gray-200"
                          >
                            <div className="flex items-start gap-1">
                              <Info size={12} className="text-purple-600 mt-0.5 flex-shrink-0" />
                              <p className="text-xs text-purple-700 italic">
                                {recommendation.reasoning}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </>
                    )}

                    {isCustom && (
                      <div className="flex items-center justify-center mt-2">
                        <Sparkles size={20} className="text-purple-600" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredRecommendations.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No stories available for this category.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    superhero: '🦸',
    fantasy: '🐉',
    mystery: '🔍',
    horror: '👻',
    'sci-fi': '🚀',
    adventure: '⚔️',
    sports: '⚽',
    science: '🔬',
    animals: '🐾',
    educational: '📚',
    occasions: '🎉',
    travel: '✈️',
    recreation: '🏖️',
    attractions: '🎢',
    custom: '✨'
  };
  return icons[category] || '📖';
}
